/**
 * ============================================================================
 * VOCALIRA CORE CONVERSATION ENGINE (VCCE) - Mobile Diagnostics
 * ============================================================================
 * 
 * SPRINT 2: Mobile-First Voice Engine
 * 
 * Este módulo fornece diagnósticos detalhados para debugging em mobile.
 * Os logs são temporários e serão removidos após validação.
 * 
 * @author Vocalira Team
 * @version 2.1.0 - Mobile-First Architecture
 */

import { getPlatformConfig } from './PlatformConfigurationManager.js';

// ============================================================================
// DIAGNOSTIC LEVELS
// ============================================================================

const DiagnosticLevel = {
  CRITICAL: 'CRITICAL',   // Erros que impedem funcionamento
  WARNING: 'WARNING',     // Problemas que podem afetar experiência
  INFO: 'INFO',           // Informações de fluxo
  DEBUG: 'DEBUG',         // Detalhes para debugging
  METRIC: 'METRIC',       // Métricas de performance
};

// ============================================================================
// MOBILE DIAGNOSTICS CLASS
// ============================================================================

/**
 * Sistema de diagnóstico móvel.
 * Coleta e reporta informações detalhadas sobre o pipeline de voz.
 */
class MobileDiagnostics {
  constructor(options = {}) {
    this.platformConfig = getPlatformConfig();
    this.platform = this.platformConfig.getPlatform();
    this.config = this.platformConfig.getConfig();
    
    // Configuração
    this.enabled = options.enabled !== false;
    this.logToConsole = options.logToConsole !== false;
    this.collectMetrics = options.collectMetrics !== false;
    
    // Storage de logs
    this.logs = [];
    this.maxLogs = options.maxLogs || 500;
    
    // Métricas de tempo
    this.timers = {};
    this.metrics = {
      // Tempos por etapa
      times: {
        permissionRequest: [],
        audioContextCreation: [],
        mediaStreamAcquisition: [],
        noiseFloorEstimation: [],
        sttInitialization: [],
        firstTranscript: [],
        turnDetection: [],
        llmResponse: [],
        ttsPlayback: [],
      },
      // Contadores
      counts: {
        sttRestarts: 0,
        recoveryAttempts: 0,
        errors: 0,
        turnsCompleted: 0,
      },
      // Estados
      states: {
        audioContextState: null,
        recognitionActive: false,
        mediaStreamActive: false,
      },
    };
    
    // Session ID para correlação
    this.sessionId = this._generateSessionId();
    
    // Log inicial
    this._logInitialization();
  }
  
  /**
   * Gera ID único para a sessão.
   */
  _generateSessionId() {
    return `vocalira_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  /**
   * Log de inicialização com informações da plataforma.
   */
  _logInitialization() {
    this.log(DiagnosticLevel.INFO, 'DIAGNOSTICS_INITIALIZED', {
      sessionId: this.sessionId,
      platform: this.platform.platformId,
      device: {
        isMobile: this.platform.isMobile,
        isTablet: this.platform.isTablet,
        isIOS: this.platform.isIOS,
        isAndroid: this.platform.isAndroid,
        isPWA: this.platform.isPWA,
        isWebView: this.platform.isWebView,
        iOSVersion: this.platform.iOSVersion,
        androidVersion: this.platform.androidVersion,
      },
      browser: {
        isChrome: this.platform.isChrome,
        isSafari: this.platform.isSafari,
        isFirefox: this.platform.isFirefox,
        isEdge: this.platform.isEdge,
        isSamsungInternet: this.platform.isSamsungInternet,
      },
      capabilities: {
        hasGetUserMedia: this.platform.hasGetUserMedia,
        hasSpeechRecognition: this.platform.hasSpeechRecognition,
        hasAudioContext: this.platform.hasAudioContext,
        isSecureContext: this.platform.isSecureContext,
      },
      config: {
        vadSpeechThreshold: this.config.vadSpeechThreshold,
        vadSilenceThreshold: this.config.vadSilenceThreshold,
        sttRestartDelay: this.config.sttRestartDelay,
        requiresUserGestureUnlock: this.config.requiresUserGestureUnlock,
      },
      userAgent: this.platform.userAgent,
    });
  }
  
  /**
   * Registra um log de diagnóstico.
   * @param {string} level - Nível do diagnóstico
   * @param {string} event - Nome do evento
   * @param {Object} data - Dados adicionais
   */
  log(level, event, data = {}) {
    if (!this.enabled) return;
    
    const entry = {
      timestamp: Date.now(),
      isoTime: new Date().toISOString(),
      sessionId: this.sessionId,
      level,
      event,
      data,
      platform: this.platform.platformId,
    };
    
    // Adicionar ao storage (circular buffer)
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
    
    // Log no console
    if (this.logToConsole) {
      const prefix = `[VOCALIRA_MOBILE] [${level}] ${event}`;
      const style = this._getLogStyle(level);
      
      if (level === DiagnosticLevel.CRITICAL) {
        console.error(prefix, data);
      } else if (level === DiagnosticLevel.WARNING) {
        console.warn(prefix, data);
      } else {
        console.log(`%c${prefix}`, style, data);
      }
    }
  }
  
  /**
   * Retorna estilo CSS para log no console.
   */
  _getLogStyle(level) {
    switch (level) {
      case DiagnosticLevel.CRITICAL:
        return 'color: #ff0000; font-weight: bold;';
      case DiagnosticLevel.WARNING:
        return 'color: #ff9900; font-weight: bold;';
      case DiagnosticLevel.INFO:
        return 'color: #0099ff;';
      case DiagnosticLevel.DEBUG:
        return 'color: #999999;';
      case DiagnosticLevel.METRIC:
        return 'color: #00cc00;';
      default:
        return 'color: #333333;';
    }
  }
  
  // ===========================================================================
  // TIMERS PARA MÉTRICAS
  // ===========================================================================
  
  /**
   * Inicia um timer para medir duração de uma operação.
   * @param {string} name - Nome do timer
   */
  startTimer(name) {
    this.timers[name] = performance.now();
    this.log(DiagnosticLevel.DEBUG, 'TIMER_STARTED', { name });
  }
  
  /**
   * Para um timer e registra a duração.
   * @param {string} name - Nome do timer
   * @returns {number} - Duração em ms
   */
  endTimer(name) {
    if (!this.timers[name]) {
      this.log(DiagnosticLevel.WARNING, 'TIMER_NOT_FOUND', { name });
      return 0;
    }
    
    const duration = performance.now() - this.timers[name];
    delete this.timers[name];
    
    // Armazenar métrica
    if (this.collectMetrics && this.metrics.times[name]) {
      this.metrics.times[name].push(duration);
      
      // Manter apenas últimas 100 medições
      if (this.metrics.times[name].length > 100) {
        this.metrics.times[name].shift();
      }
    }
    
    this.log(DiagnosticLevel.METRIC, 'TIMER_ENDED', { 
      name, 
      duration: Math.round(duration),
      unit: 'ms',
    });
    
    return duration;
  }
  
  // ===========================================================================
  // EVENTOS ESPECÍFICOS DO PIPELINE
  // ===========================================================================
  
  /**
   * Log de permissão de microfone.
   */
  logPermission(status, error = null) {
    const level = status === 'granted' ? DiagnosticLevel.INFO : DiagnosticLevel.WARNING;
    this.log(level, 'PERMISSION_RESULT', { status, error: error?.message });
  }
  
  /**
   * Log de AudioContext.
   */
  logAudioContext(event, data = {}) {
    this.metrics.states.audioContextState = data.state;
    this.log(DiagnosticLevel.INFO, `AUDIOCONTEXT_${event}`, {
      ...data,
      platform: this.platform.platformId,
    });
  }
  
  /**
   * Log de MediaStream.
   */
  logMediaStream(event, data = {}) {
    this.metrics.states.mediaStreamActive = event === 'ACQUIRED';
    this.log(DiagnosticLevel.INFO, `MEDIASTREAM_${event}`, data);
  }
  
  /**
   * Log de SpeechRecognition.
   */
  logSTT(event, data = {}) {
    const level = event.includes('ERROR') || event.includes('FAILED') 
      ? DiagnosticLevel.WARNING 
      : DiagnosticLevel.INFO;
    
    if (event === 'RESTART') {
      this.metrics.counts.sttRestarts++;
    }
    
    this.metrics.states.recognitionActive = event === 'STARTED';
    this.log(level, `STT_${event}`, data);
  }
  
  /**
   * Log de VAD.
   */
  logVAD(event, data = {}) {
    this.log(DiagnosticLevel.DEBUG, `VAD_${event}`, data);
  }
  
  /**
   * Log de Turn Detection.
   */
  logTurnDetection(event, data = {}) {
    if (event === 'COMPLETED') {
      this.metrics.counts.turnsCompleted++;
    }
    this.log(DiagnosticLevel.INFO, `TURN_${event}`, data);
  }
  
  /**
   * Log de Recovery.
   */
  logRecovery(event, data = {}) {
    this.metrics.counts.recoveryAttempts++;
    const level = event.includes('FAILED') ? DiagnosticLevel.WARNING : DiagnosticLevel.INFO;
    this.log(level, `RECOVERY_${event}`, data);
  }
  
  /**
   * Log de erro.
   */
  logError(component, error, context = {}) {
    this.metrics.counts.errors++;
    this.log(DiagnosticLevel.CRITICAL, `ERROR_${component}`, {
      error: error?.message || error,
      stack: error?.stack,
      ...context,
    });
  }
  
  // ===========================================================================
  // RELATÓRIOS
  // ===========================================================================
  
  /**
   * Gera relatório completo de diagnóstico.
   */
  generateReport() {
    const report = {
      sessionId: this.sessionId,
      generatedAt: new Date().toISOString(),
      platform: this.platformConfig.getDiagnosticReport(),
      metrics: {
        times: this._calculateTimeMetrics(),
        counts: { ...this.metrics.counts },
        states: { ...this.metrics.states },
      },
      recentLogs: this.logs.slice(-50),
      summary: this._generateSummary(),
    };
    
    this.log(DiagnosticLevel.INFO, 'REPORT_GENERATED', { 
      logsCount: this.logs.length,
      turnsCompleted: this.metrics.counts.turnsCompleted,
    });
    
    return report;
  }
  
  /**
   * Calcula métricas de tempo (médias, min, max).
   */
  _calculateTimeMetrics() {
    const result = {};
    
    for (const [name, values] of Object.entries(this.metrics.times)) {
      if (values.length === 0) {
        result[name] = null;
        continue;
      }
      
      const sorted = [...values].sort((a, b) => a - b);
      result[name] = {
        count: values.length,
        avg: Math.round(values.reduce((a, b) => a + b, 0) / values.length),
        min: Math.round(sorted[0]),
        max: Math.round(sorted[sorted.length - 1]),
        p50: Math.round(sorted[Math.floor(sorted.length * 0.5)]),
        p95: Math.round(sorted[Math.floor(sorted.length * 0.95)]),
      };
    }
    
    return result;
  }
  
  /**
   * Gera resumo de saúde do pipeline.
   */
  _generateSummary() {
    const errorRate = this.metrics.counts.turnsCompleted > 0
      ? this.metrics.counts.errors / this.metrics.counts.turnsCompleted
      : 0;
    
    const restartRate = this.metrics.counts.turnsCompleted > 0
      ? this.metrics.counts.sttRestarts / this.metrics.counts.turnsCompleted
      : 0;
    
    let health = 'GOOD';
    const issues = [];
    
    if (errorRate > 0.1) {
      health = 'POOR';
      issues.push(`High error rate: ${(errorRate * 100).toFixed(1)}%`);
    } else if (errorRate > 0.05) {
      health = 'FAIR';
      issues.push(`Elevated error rate: ${(errorRate * 100).toFixed(1)}%`);
    }
    
    if (restartRate > 0.5) {
      health = health === 'GOOD' ? 'FAIR' : health;
      issues.push(`High STT restart rate: ${(restartRate * 100).toFixed(1)}%`);
    }
    
    return {
      health,
      issues,
      stats: {
        turnsCompleted: this.metrics.counts.turnsCompleted,
        errorRate: `${(errorRate * 100).toFixed(1)}%`,
        restartRate: `${(restartRate * 100).toFixed(1)}%`,
        recoveryAttempts: this.metrics.counts.recoveryAttempts,
      },
    };
  }
  
  /**
   * Exporta logs para download (útil para debugging em produção).
   */
  exportLogs() {
    const report = this.generateReport();
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `vocalira_diagnostics_${this.sessionId}.json`;
    a.click();
    
    URL.revokeObjectURL(url);
    
    this.log(DiagnosticLevel.INFO, 'LOGS_EXPORTED');
  }
  
  /**
   * Limpa logs antigos.
   */
  clearLogs() {
    this.logs = [];
    this.log(DiagnosticLevel.INFO, 'LOGS_CLEARED');
  }
  
  /**
   * Desabilita diagnósticos (para produção após validação).
   */
  disable() {
    this.enabled = false;
    this.logToConsole = false;
  }
  
  /**
   * Habilita diagnósticos.
   */
  enable() {
    this.enabled = true;
    this.logToConsole = true;
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

let _diagnosticsInstance = null;

/**
 * Obtém a instância singleton de MobileDiagnostics.
 */
function getDiagnostics() {
  if (!_diagnosticsInstance) {
    _diagnosticsInstance = new MobileDiagnostics();
  }
  return _diagnosticsInstance;
}

// ============================================================================
// EXPORTS
// ============================================================================

export default MobileDiagnostics;
export { 
  getDiagnostics, 
  DiagnosticLevel,
  MobileDiagnostics,
};
